import React, { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../helpers/useAuth";
import { getSession } from "../endpoints/auth/session_GET.schema";
import { User } from "../helpers/User";
import { AuthErrorPage } from "./AuthErrorPage";
import { ShieldOff } from "lucide-react";
import { AuthLoadingState } from "./AuthLoadingState";
import styles from "./ProtectedRoute.module.css";

// Do not use this in pageLayout
const MakeProtectedRoute: (roles: User["role"][]) => React.FC<{
  children: React.ReactNode;
}> =
  (roles) =>
  ({ children }) => {
    const { authState, onLogin } = useAuth();
    const location = useLocation();
    // useAuth ne vérifie la session que si le drapeau localStorage has_session existe.
    // Ce drapeau peut disparaître alors que le cookie de session (HttpOnly) reste valide :
    // Safari vide le stockage après 7 jours sans visite, nettoyage du navigateur...
    // Avant de renvoyer un membre vers /inscription/, on demande donc une fois au serveur.
    const [verification, setVerification] = useState<"a-faire" | "en-cours" | "faite">("a-faire");
    const anonymeSansVerification = authState.type === "unauthenticated" && !authState.errorMessage;

    useEffect(() => {
      if (!anonymeSansVerification || verification !== "a-faire") return;
      setVerification("en-cours");
      getSession()
        .then((result) => {
          if ("user" in result && result.user) onLogin(result.user);
        })
        .catch(() => { /* anonyme ou serveur indisponible : redirection normale */ })
        .finally(() => setVerification("faite"));
    }, [anonymeSansVerification, verification, onLogin]);

    // Show loading state while checking authentication
    if (authState.type === "loading") {
      return <AuthLoadingState title="Authenticating" />;
    }

    // Anonyme → /inscription/ (avec ?next= pour retour post-login).
    // Plus convertissant que /login/ pour un visiteur qui découvre un outil gated.
    // La page d'inscription affiche un switch clair vers /login/ pour les déjà-inscrits.
    // Exception : /compte (lien d'un email, session expirée) n'est ouvert que par un membre
    // déjà inscrit, il arrive donc sur /login/.
    if (anonymeSansVerification && verification !== "faite") {
      return <AuthLoadingState title="Authenticating" />;
    }

    if (authState.type === "unauthenticated") {
      const next = encodeURIComponent(location.pathname + location.search);
      const cible = /^\/compte(\/|$)/.test(location.pathname) ? "/login/" : "/inscription/";
      return <Navigate to={`${cible}?next=${next}`} replace />;
    }

    if (!roles.includes(authState.user.role)) {
      return (
        <AuthErrorPage
          title="Access Denied"
          message={`Access denied. Your role (${authState.user.role}) lacks required permissions.`}
          icon={<ShieldOff className={styles.accessDeniedIcon} size={64} />}
        />
      );
    }

    // Render children if authenticated
    return <>{children}</>;
  };

// Create protected routes here, then import them in pageLayout
export const AdminRoute = MakeProtectedRoute(["admin"]);
export const UserRoute = MakeProtectedRoute(["user", "admin"]);
